import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Bell, BellRinging, ChatCircleDots, Check } from "@phosphor-icons/react";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
} from "@/components/ui/dropdown-menu";

function ago(iso) {
  if (!iso) return "";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "à l'instant";
  if (s < 3600) return `il y a ${Math.floor(s / 60)} min`;
  if (s < 86400) return `il y a ${Math.floor(s / 3600)} h`;
  return `il y a ${Math.floor(s / 86400)} j`;
}

export default function NotificationBell() {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const timer = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications");
      setItems(data.items || []);
      setUnread(data.unread || 0);
    } catch { /* silent — bell is non-critical */ }
  }, []);

  useEffect(() => {
    load();
    timer.current = setInterval(load, 60000);
    return () => clearInterval(timer.current);
  }, [load]);

  const openItem = async (n) => {
    setOpen(false);
    if (!n.is_read) {
      try { await api.post(`/notifications/${n.id}/read`); } catch { /* noop */ }
      setUnread((u) => Math.max(0, u - 1));
      setItems((arr) => arr.map((x) => (x.id === n.id ? { ...x, is_read: true } : x)));
    }
    navigate(n.link || "/app/messagerie");
  };

  const readAll = async () => {
    try { await api.post("/notifications/read-all"); } catch { /* noop */ }
    setUnread(0);
    setItems((arr) => arr.map((x) => ({ ...x, is_read: true })));
  };

  return (
    <DropdownMenu open={open} onOpenChange={(v) => { setOpen(v); if (v) load(); }}>
      <DropdownMenuTrigger asChild>
        <button data-testid="notif-bell"
          className="relative flex items-center justify-center w-9 h-9 rounded-full hover:bg-surface-2 transition-colors">
          {unread > 0
            ? <BellRinging size={19} weight="fill" className="text-accent" />
            : <Bell size={19} className="text-muted" />}
          {unread > 0 && (
            <span data-testid="notif-badge"
              className="absolute -top-0.5 -right-0.5 min-w-[17px] h-[17px] px-1 flex items-center justify-center rounded-full bg-accent text-black text-[10px] font-bold font-mono">
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 p-0 bg-surface border-border" data-testid="notif-menu">
        <div className="flex items-center justify-between px-3 py-2.5 border-b border-border">
          <span className="font-display font-bold text-sm text-fg">Notifications</span>
          {unread > 0 && (
            <button onClick={readAll} data-testid="notif-read-all"
              className="text-xs text-accent hover:underline flex items-center gap-1">
              <Check size={12} /> Tout lire
            </button>
          )}
        </div>
        <div className="max-h-[380px] overflow-y-auto">
          {items.length === 0 ? (
            <div className="px-4 py-8 text-center text-muted text-xs" data-testid="notif-empty">
              <ChatCircleDots size={26} weight="duotone" className="mx-auto mb-2 text-muted/60" />
              Aucune notification.<br />Vous serez alerté des réponses à vos emails.
            </div>
          ) : items.map((n) => (
            <button key={n.id} onClick={() => openItem(n)} data-testid={`notif-item-${n.id}`}
              className={`w-full text-left px-3 py-2.5 border-b border-border/50 hover:bg-surface-2/60 transition-colors flex gap-2.5 ${n.is_read ? "" : "bg-accent/[0.04]"}`}>
              <span className={`mt-1 shrink-0 w-1.5 h-1.5 rounded-full ${n.is_read ? "bg-transparent" : "bg-accent"}`} />
              <span className="min-w-0 flex-1">
                <span className="block text-sm text-fg truncate">{n.title}</span>
                {n.body && <span className="block text-xs text-muted truncate">{n.body}</span>}
                <span className="block text-[10px] text-muted/70 font-mono mt-0.5">{ago(n.at)}</span>
              </span>
            </button>
          ))}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
